import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import {
    AvailabilityWindow,
    ScheduledSession,
    ScheduledStatus,
} from "../../domain/types";
import { weekDayKeys, weekdayOfDateKey } from "../../domain/formatting";
import type { WhatsAppReminderAction } from "../../domain/whatsappReminder";
import WeekSessionSheet from "./WeekSessionSheet";
import {
    freeSpans,
    isSpanPast,
    layoutDayEvents,
    minutesOfDayTime,
    visibleHourRange,
    WeekSpan,
} from "../../domain/weekLayout";

interface WeekGridProps {
    /** Monday of the week on screen, `YYYY-MM-DD`. */
    weekStart: string;
    /** `YYYY-MM-DD` → that day's sessions, ascending by hour. */
    sessionsByDay: Map<string, ScheduledSession[]>;
    availability: readonly AvailabilityWindow[];
    /** Today in the therapist's timezone, so "today" is not the browser's guess. */
    todayKey: string;
    /** Minutes since midnight now, in the therapist's timezone. */
    nowMinute: number;
    sessionLabel: (session: ScheduledSession) => string;
    spaceName: (session: ScheduledSession) => string | undefined;
    /**
     * How long a new booking lasts, so hovering previews the whole block it
     * would take — as a calendar app does — not just its starting quarter.
     */
    newSessionMinutes?: number;
    /** `minute` is minutes since midnight of the quarter hour that was clicked. */
    onCreateOnDay: (day: string, minute: number) => void;
    onCancel: (session: ScheduledSession) => void;
    isCancelling?: boolean;
    /** Moving the session to another time. Omitted where it is not offered. */
    onReschedule?: (session: ScheduledSession) => void;
    isRescheduling?: boolean;
    /**
     * The WhatsApp button of a booking: the patient's chat with a reminder
     * already written, a patient who does not allow WhatsApp, or undefined
     * when there is no one to write to.
     */
    whatsappReminder?: (session: ScheduledSession) => WhatsAppReminderAction | undefined;
    /** Pressed on a patient who switched WhatsApp off: warn the therapist. */
}

/** One hour of grid, in pixels. Everything else is derived from this. */
const HOUR_HEIGHT = 52;

/**
 * A block needs this much height for the name and the time on two lines
 * (two 15px lines and the block's padding). A shorter one — a half-hour
 * session is 26px — keeps the name only; the time spilling out of it
 * would land on whatever is booked next.
 */
const TWO_LINE_MIN_HEIGHT = 38;

/** Monday = 0, so Saturday and Sunday are 5 and 6. */
const WEEKEND = new Set([5, 6]);

/** Column templates: gutter plus seven days, or five on a phone with a free weekend. */
const FULL_WEEK_COLUMNS =
    "grid-cols-[2rem_repeat(7,minmax(0,1fr))] sm:grid-cols-[3.25rem_repeat(7,minmax(0,1fr))]";
const WORK_WEEK_COLUMNS =
    "grid-cols-[2rem_repeat(5,minmax(0,1fr))] sm:grid-cols-[3.25rem_repeat(7,minmax(0,1fr))]";

/** Written out whole, so Tailwind sees both classes. */
const PHONE_HIDDEN = { flex: "hidden sm:flex", block: "hidden sm:block" } as const;

/**
 * `María García` → `MG`: what fits a block sharing its ~45px column with
 * another booking, where even a first name breaks into single letters.
 */
function initialsOf(label: string): string {
    return label
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((word) => word.charAt(0).toUpperCase())
        .join("");
}

/**
 * A phone block needs this much height to wrap a full name over three
 * lines (three 15px lines and its padding); a shorter one gets the first
 * name, which is what fits and what the therapist calls the patient.
 */
const FULL_NAME_MIN_PHONE_HEIGHT = 49;

/** `HH:MM` for minutes since midnight. */
function minuteLabel(minute: number): string {
    const pad = (value: number) => `${value}`.padStart(2, "0");
    return `${pad(Math.floor(minute / 60))}:${pad(minute % 60)}`;
}

/**
 * Which quarter of an hour cell the pointer is in, 0-3. A keyboard activation
 * (`detail` 0) has no pointer position, so it means the hour itself.
 */
function pointedQuarter(event: React.MouseEvent<HTMLElement>): number {
    if (event.type === "click" && event.detail === 0) return 0;
    const { top, height } = event.currentTarget.getBoundingClientRect();
    if (height <= 0) return 0;
    const fraction = (event.clientY - top) / height;
    return Math.min(3, Math.max(0, Math.floor(fraction * 4)));
}

const STATUS_STYLES: Record<ScheduledStatus, string> = {
    [ScheduledStatus.SCHEDULED]:
        "border-brand-500 bg-brand-500/90 text-white hover:bg-brand-500",
    [ScheduledStatus.COMPLETED]:
        "border-green-600 bg-green-500/90 text-white hover:bg-green-500",
    [ScheduledStatus.CANCELLED]:
        "border-gray-300 bg-gray-200 text-gray-500 line-through dark:border-gray-600 dark:bg-gray-700 dark:text-gray-400",
};

/**
 * Sessions that have already happened, drawn in a tint of the same colour.
 *
 * Still legible, still the same hue — the week just stops competing for
 * attention with what is already done, so the eye lands on what is left.
 */
const PAST_STATUS_STYLES: Record<ScheduledStatus, string> = {
    [ScheduledStatus.SCHEDULED]:
        "border-brand-300 bg-brand-100/80 text-brand-700 hover:bg-brand-100 dark:border-brand-500/50 dark:bg-brand-400/10 dark:text-brand-200",
    [ScheduledStatus.COMPLETED]:
        "border-green-300 bg-green-100/80 text-green-700 hover:bg-green-100 dark:border-green-500/50 dark:bg-green-400/10 dark:text-green-200",
    [ScheduledStatus.CANCELLED]: STATUS_STYLES[ScheduledStatus.CANCELLED],
};

/**
 * A Monday-to-Sunday time grid: hours down the side, days across the top, each
 * booking drawn where and how long it actually is.
 *
 * The month view answers "which days am I busy"; this one answers "how does
 * Tuesday actually look", which is the question a therapist arranging a week
 * is really asking — hence it being the view the page opens on.
 */
export default function WeekGrid({
    weekStart,
    sessionsByDay,
    availability,
    todayKey,
    nowMinute,
    sessionLabel,
    spaceName,
    newSessionMinutes = 60,
    onCreateOnDay,
    onCancel,
    isCancelling = false,
    onReschedule,
    isRescheduling = false,
    whatsappReminder,
}: WeekGridProps) {
    const { t, i18n } = useTranslation();
    /** The quarter hour under the pointer, previewed before it is clicked. */
    const [hovered, setHovered] = useState<{ day: string; minute: number } | null>(
        null
    );

    /** The booking tapped on a phone, whose details and actions are open. */
    const [opened, setOpened] = useState<{
        session: ScheduledSession;
        day: string;
    } | null>(null);

    const days = useMemo(() => weekDayKeys(weekStart), [weekStart]);

    /** Every span on screen — bookings and working hours alike — sizes the grid. */
    const { startHour, endHour } = useMemo(() => {
        const spans: WeekSpan[] = [];
        days.forEach((day) => {
            (sessionsByDay.get(day) ?? []).forEach((session) => {
                const startMinute = minutesOfDayTime(session.hour);
                spans.push({
                    startMinute,
                    endMinute: startMinute + session.duration,
                });
            });
        });
        availability
            .filter((window) => window.is_active)
            .forEach((window) =>
                spans.push({
                    startMinute: minutesOfDayTime(window.start_time),
                    endMinute: minutesOfDayTime(window.end_time),
                })
            );
        return visibleHourRange(spans);
    }, [days, sessionsByDay, availability]);

    const gridStartMinute = startHour * 60;
    const totalMinutes = (endHour - startHour) * 60;
    const gridHeight = (endHour - startHour) * HOUR_HEIGHT;

    /** Fraction of the grid a minute offset sits at, clamped to the grid. */
    const offsetOf = (minute: number) =>
        ((Math.min(Math.max(minute, gridStartMinute), gridStartMinute + totalMinutes) -
            gridStartMinute) /
            totalMinutes) *
        gridHeight;

    const hours = Array.from(
        { length: endHour - startHour },
        (_, index) => startHour + index
    );

    const windowsByWeekday = useMemo(() => {
        const byWeekday = new Map<number, AvailabilityWindow[]>();
        availability
            .filter((window) => window.is_active)
            .forEach((window) =>
                byWeekday.set(window.day_of_week, [
                    ...(byWeekday.get(window.day_of_week) ?? []),
                    window,
                ])
            );
        return byWeekday;
    }, [availability]);

    /**
     * On a phone seven columns leave ~45px each. A weekend with no working
     * hours and nothing booked is two empty columns, so there it is dropped
     * and the five working days share the width. From `sm` up the whole week
     * always shows.
     */
    const hideWeekendOnPhone = useMemo(
        () =>
            !availability.some(
                (window) => window.is_active && WEEKEND.has(window.day_of_week)
            ) &&
            days.every(
                (day) =>
                    !WEEKEND.has(weekdayOfDateKey(day)) ||
                    (sessionsByDay.get(day) ?? []).length === 0
            ),
        [availability, days, sessionsByDay]
    );
    const columns = hideWeekendOnPhone ? WORK_WEEK_COLUMNS : FULL_WEEK_COLUMNS;
    /** Hides a weekend column on a phone, when the week drops it. */
    const phoneHidden = (day: string, display: "flex" | "block") =>
        hideWeekendOnPhone && WEEKEND.has(weekdayOfDateKey(day))
            ? PHONE_HIDDEN[display]
            : "";

    const dayHeading = (day: string) =>
        new Date(`${day}T12:00:00`).toLocaleDateString(i18n.language, {
            weekday: "short",
        });

    return (
        <div className="flex flex-1 flex-col overflow-auto">
            {/* Day headers stay put while the hours scroll under them. */}
            <div className={`sticky top-0 z-20 grid ${columns} border-b border-gray-200 bg-white/85 backdrop-blur dark:border-white/10 dark:bg-gray-900/85`}>
                <div />
                {days.map((day) => {
                    const isToday = day === todayKey;
                    return (
                        <div
                            key={day}
                            className={`flex flex-col items-center gap-0.5 border-l border-gray-100 py-1.5 dark:border-white/5 sm:py-2 ${phoneHidden(day, "flex")}`}
                        >
                            <span className="text-xs font-medium uppercase tracking-wide text-content-tertiary">
                                <span className="sm:hidden">
                                    {dayHeading(day).charAt(0)}
                                </span>
                                <span className="hidden sm:inline">
                                    {dayHeading(day)}
                                </span>
                            </span>
                            <span
                                className={`flex h-6 w-6 items-center justify-center rounded-full text-xs font-semibold sm:h-7 sm:w-7 sm:text-sm ${
                                    isToday
                                        ? "bg-brand-600 text-white"
                                        : "text-gray-700 dark:text-gray-200"
                                }`}
                            >
                                {Number(day.slice(8, 10))}
                            </span>
                        </div>
                    );
                })}
            </div>

            <div className={`grid ${columns}`}>
                {/* Hour gutter */}
                <div className="relative" style={{ height: gridHeight }}>
                    {hours.map((hour, index) => (
                        <div
                            key={hour}
                            className="absolute right-1 -translate-y-1/2 text-xs tabular-nums text-content-tertiary sm:right-2"
                            style={{ top: index * HOUR_HEIGHT }}
                        >
                            {index === 0 ? (
                                ""
                            ) : (
                                <>
                                    <span className="sm:hidden">
                                        {`${hour}`.padStart(2, "0")}
                                    </span>
                                    <span className="hidden sm:inline">
                                        {`${`${hour}`.padStart(2, "0")}:00`}
                                    </span>
                                </>
                            )}
                        </div>
                    ))}
                </div>

                {days.map((day) => {
                    const sessions = sessionsByDay.get(day) ?? [];
                    const spans: WeekSpan[] = sessions.map((session) => {
                        const startMinute = minutesOfDayTime(session.hour);
                        return {
                            startMinute,
                            endMinute: startMinute + session.duration,
                        };
                    });
                    const placements = layoutDayEvents(spans);
                    const windows = windowsByWeekday.get(weekdayOfDateKey(day)) ?? [];
                    const workingSpans: WeekSpan[] = windows.map((window) => ({
                        startMinute: minutesOfDayTime(window.start_time),
                        endMinute: minutesOfDayTime(window.end_time),
                    }));
                    // A cancelled session frees its time again, so it does not
                    // block the gap it used to occupy.
                    const bookedSpans = spans.filter(
                        (_, index) =>
                            sessions[index].scheduled_status !==
                            ScheduledStatus.CANCELLED
                    );
                    // Nothing before now is bookable, so nothing before now is
                    // marked free — including every hour of a day already past.
                    const notBefore =
                        day < todayKey
                            ? Number.POSITIVE_INFINITY
                            : day === todayKey
                              ? nowMinute
                              : Number.NEGATIVE_INFINITY;
                    const free = freeSpans(workingSpans, bookedSpans, notBefore);

                    return (
                        <div
                            key={day}
                            className={`relative border-l border-gray-100 dark:border-white/5 ${phoneHidden(day, "block")}`}
                            style={{ height: gridHeight }}
                        >
                            {/* Working hours, as a plain wash */}
                            {workingSpans.map((window) => {
                                const top = offsetOf(window.startMinute);
                                return (
                                    <div
                                        key={`work-${window.startMinute}`}
                                        aria-hidden="true"
                                        className="absolute inset-x-0 bg-gray-50/80 dark:bg-white/[0.03]"
                                        style={{
                                            top,
                                            height:
                                                offsetOf(window.endMinute) - top,
                                        }}
                                    />
                                );
                            })}

                            {/* The gaps that can actually be booked, so free
                                time is visible before anything is clicked. */}
                            {free.map((span) => {
                                const top = offsetOf(span.startMinute);
                                return (
                                    <div
                                        key={`free-${span.startMinute}`}
                                        aria-hidden="true"
                                        data-free="true"
                                        className="pointer-events-none absolute inset-x-0.5 rounded-md bg-brand-50 ring-1 ring-inset ring-brand-200/70 dark:bg-brand-400/10 dark:ring-brand-400/25"
                                        style={{
                                            top,
                                            height: Math.max(
                                                offsetOf(span.endMinute) - top,
                                                6
                                            ),
                                        }}
                                    />
                                );
                            })}

                            {/* One hit target per hour, behind the blocks: the
                                quarter of the hour clicked is the time the
                                booking opens on. A keyboard press has no
                                pointer, so it opens on the hour. */}
                            {hours.map((hour, index) => (
                                <button
                                    key={hour}
                                    type="button"
                                    onClick={(event) =>
                                        onCreateOnDay(
                                            day,
                                            hour * 60 +
                                                pointedQuarter(event) * 15
                                        )
                                    }
                                    onMouseMove={(event) => {
                                        const minute =
                                            hour * 60 + pointedQuarter(event) * 15;
                                        if (
                                            hovered?.day !== day ||
                                            hovered.minute !== minute
                                        ) {
                                            setHovered({ day, minute });
                                        }
                                    }}
                                    onMouseLeave={() => setHovered(null)}
                                    aria-label={t(
                                        "scheduler.calendar.newSessionAt",
                                        {
                                            date: day,
                                            time: `${`${hour}`.padStart(2, "0")}:00`,
                                        }
                                    )}
                                    className="absolute inset-x-0 cursor-pointer border-t border-gray-100 dark:border-white/5"
                                    style={{
                                        top: index * HOUR_HEIGHT,
                                        height: HOUR_HEIGHT,
                                    }}
                                />
                            ))}

                            {hovered?.day === day && (
                                <div
                                    aria-hidden="true"
                                    data-hover-preview="true"
                                    className="pointer-events-none absolute inset-x-0.5 overflow-hidden rounded-md bg-brand-100/70 px-1 py-0.5 text-xs font-medium tabular-nums leading-tight text-brand-700 ring-1 ring-inset ring-brand-300 dark:bg-brand-400/20 dark:text-brand-200 dark:ring-brand-400/40"
                                    style={{
                                        top: offsetOf(hovered.minute),
                                        height:
                                            offsetOf(hovered.minute + newSessionMinutes) -
                                            offsetOf(hovered.minute),
                                    }}
                                >
                                    {`${minuteLabel(hovered.minute)} – ${minuteLabel(
                                        hovered.minute + newSessionMinutes
                                    )}`}
                                </div>
                            )}

                            {day === todayKey && (
                                <div
                                    aria-hidden="true"
                                    className="absolute inset-x-0 z-10 border-t-2 border-red-500"
                                    style={{ top: offsetOf(nowMinute) }}
                                >
                                    <span className="absolute -left-1 -top-1 h-2 w-2 rounded-full bg-red-500" />
                                </div>
                            )}

                            {sessions.map((session, index) => {
                                const placement = placements[index];
                                const span = spans[index];
                                const top = offsetOf(span.startMinute);
                                const height = Math.max(
                                    offsetOf(span.endMinute) - top,
                                    18
                                );
                                const width = 100 / placement.lanes;
                                const label = sessionLabel(session);
                                const phoneLabel =
                                    placement.lanes > 1
                                        ? initialsOf(label)
                                        : height >= FULL_NAME_MIN_PHONE_HEIGHT
                                          ? label
                                          : (label.split(/\s+/)[0] ?? label);
                                const place = spaceName(session);
                                const past = isSpanPast(
                                    day,
                                    span,
                                    todayKey,
                                    nowMinute
                                );
                                const palette = past
                                    ? PAST_STATUS_STYLES
                                    : STATUS_STYLES;

                                return (
                                    <article
                                        key={session.id}
                                        data-past={past || undefined}
                                        title={`${session.hour.slice(0, 5)} · ${label}`}
                                        className={`absolute z-10 overflow-hidden rounded border-l-2 px-0.5 py-0.5 text-left shadow-sm transition-colors sm:rounded-md sm:border-l-4 sm:px-1.5 sm:py-1 ${
                                            palette[session.scheduled_status] ??
                                            palette[ScheduledStatus.SCHEDULED]
                                        }`}
                                        style={{
                                            top,
                                            height,
                                            left: `calc(${placement.lane * width}% + 2px)`,
                                            width: `calc(${width}% - 4px)`,
                                        }}
                                    >
                                        {/* The whole block is the button: a
                                            click or tap opens every action in
                                            a sheet, the same on every screen.
                                            Stacked above the text, since the
                                            faded time line's opacity would
                                            otherwise paint over it and
                                            swallow the click. */}
                                        <button
                                            type="button"
                                            onClick={() =>
                                                setOpened({ session, day })
                                            }
                                            aria-label={t(
                                                "scheduler.calendar.openSessionDetails",
                                                { subject: label }
                                            )}
                                            className="absolute inset-0 z-10"
                                        />
                                        {/* The name wraps rather than
                                            truncating on a phone, hyphenated
                                            where a word does not fit, so a
                                            narrow column still says who it
                                            is. Sharing the column with
                                            another booking, it is initials. */}
                                        <p className="hyphens-auto break-words text-xs font-semibold leading-tight sm:truncate">
                                            {phoneLabel !== label ? (
                                                <>
                                                    <span className="sm:hidden">
                                                        {phoneLabel}
                                                    </span>
                                                    <span className="hidden sm:inline">
                                                        {label}
                                                    </span>
                                                </>
                                            ) : (
                                                label
                                            )}
                                        </p>
                                        {/* A ~45px phone column fits a name or
                                            a time, not both — the name wins,
                                            and the block's position already
                                            says when it is. So does a block
                                            too short for two lines. */}
                                        {height >= TWO_LINE_MIN_HEIGHT && (
                                            <p className="hidden truncate text-xs leading-tight opacity-90 sm:block">
                                                {session.hour.slice(0, 5)}
                                                {place ? ` · ${place}` : ""}
                                            </p>
                                        )}

                                    </article>
                                );
                            })}
                        </div>
                    );
                })}
            </div>

            {opened && (
                <OpenedSession
                    session={opened.session}
                    day={opened.day}
                    label={sessionLabel(opened.session)}
                    place={spaceName(opened.session)}
                    todayKey={todayKey}
                    nowMinute={nowMinute}
                    whatsappReminder={whatsappReminder}
                    onClose={() => setOpened(null)}
                    onCancel={onCancel}
                    isCancelling={isCancelling}
                    onReschedule={onReschedule}
                    isRescheduling={isRescheduling}
                />
            )}
        </div>
    );
}

interface OpenedSessionProps {
    session: ScheduledSession;
    day: string;
    label: string;
    place?: string;
    todayKey: string;
    nowMinute: number;
    whatsappReminder?: (session: ScheduledSession) => WhatsAppReminderAction | undefined;
    onClose: () => void;
    onCancel: (session: ScheduledSession) => void;
    isCancelling: boolean;
    onReschedule?: (session: ScheduledSession) => void;
    isRescheduling: boolean;
}

/**
 * `lunes, 28 de septiembre` → `Lunes, 28 de septiembre`. Only the first
 * letter: CSS `capitalize` would also give `De Septiembre`.
 */
function sentenceCase(text: string): string {
    return text.charAt(0).toUpperCase() + text.slice(1);
}

/** The sheet for a clicked or tapped booking, in the week or the month. */
export function OpenedSession({
    session,
    day,
    label,
    place,
    todayKey,
    nowMinute,
    whatsappReminder,
    ...actions
}: OpenedSessionProps) {
    const { i18n } = useTranslation();
    const startMinute = minutesOfDayTime(session.hour);
    const span = { startMinute, endMinute: startMinute + session.duration };
    const past = isSpanPast(day, span, todayKey, nowMinute);
    const cancelled = session.scheduled_status === ScheduledStatus.CANCELLED;

    return (
        <WeekSessionSheet
            session={session}
            label={label}
            place={place}
            timeRange={`${minuteLabel(span.startMinute)} – ${minuteLabel(span.endMinute)}`}
            dateLabel={sentenceCase(
                new Date(`${day}T12:00:00`).toLocaleDateString(i18n.language, {
                    weekday: "long",
                    day: "numeric",
                    month: "long",
                })
            )}
            past={past}
            reminder={!cancelled && !past ? whatsappReminder?.(session) : undefined}
            {...actions}
        />
    );
}
